// S18B-2 — e-Map (OSWC GAP-2): sites on a live map, coloured by health,
// alarm overlay, click → site detail → live camera pop-up.
//
// Reuses the same react-leaflet primitive as MapView (no new map lib) and the
// existing CameraFeedPlayer for the live pop-up (no new player).
import { useEffect, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";

import { useEmapEntities, type EMapEntity, type HealthStatus } from "@/api/emap";
import { useSiteCameras } from "@/api/sites";
import { CameraFeedPlayer } from "@/components/CameraFeedPlayer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLicense } from "@/api/license";

const HEALTH_COLOR: Record<HealthStatus, string> = {
  green: "#10b981",
  amber: "#f59e0b",
  red: "#ef4444",
  grey: "#9ca3af",
};

// A teardrop pin; when the site has an active alarm we add a pulsing ring.
function healthIcon(status: HealthStatus, alarm: boolean): L.DivIcon {
  const fill = HEALTH_COLOR[status];
  const ring = alarm
    ? `<circle cx="11" cy="11" r="10" fill="none" stroke="#ef4444" stroke-width="2">
         <animate attributeName="r" values="6;13;6" dur="1.3s" repeatCount="indefinite"/>
         <animate attributeName="opacity" values="0.9;0;0.9" dur="1.3s" repeatCount="indefinite"/>
       </circle>`
    : "";
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="32" viewBox="0 0 22 32">
      ${ring}
      <path d="M11 0 C5 0 0 5 0 11 c0 8 11 21 11 21 s11-13 11-21 c0-6-5-11-11-11 z"
            fill="${fill}" stroke="#263238" stroke-width="1.5"/>
      <circle cx="11" cy="11" r="4" fill="white"/>
    </svg>`;
  return L.divIcon({
    className: "",
    html: svg,
    iconSize: [22, 32],
    iconAnchor: [11, 32],
    popupAnchor: [0, -28],
  });
}

function FitBounds({ entities }: { entities: EMapEntity[] }) {
  const map = useMap();
  useEffect(() => {
    if (entities.length === 0) return;
    const bounds = L.latLngBounds(
      entities.map((e) => [e.lat, e.lng] as [number, number]),
    );
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
  }, [entities, map]);
  return null;
}

export default function EMapView() {
  const license = useLicense();
  const emapEnabled = license.data?.features?.["emap"] ?? true;
  // `{ data }` ALONE — the Escalation Matrix shape this component library was
  // built for. `entities = data?.entities ?? []` turns a 500 into zero sites,
  // and the header then reads "0 sites · 0 green / 0 amber / 0 red" over an
  // empty map: a clean bill of health for an estate we failed to load. The
  // `missing_coordinates` hint also silently vanishes, so the one clue that
  // something is off with the data disappears exactly when it would help.
  const { data, isLoading, isError, error, refetch } = useEmapEntities();
  const [liveCam, setLiveCam] = useState<{ name: string; hlsUrl?: string | null } | null>(
    null,
  );
  const [camListSite, setCamListSite] = useState<EMapEntity | null>(null);

  const entities = data?.entities ?? [];
  const counts = entities.reduce(
    (acc, e) => {
      acc[e.health_status] = (acc[e.health_status] ?? 0) + 1;
      return acc;
    },
    { green: 0, amber: 0, red: 0, grey: 0 } as Record<HealthStatus, number>,
  );

  if (!emapEnabled) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          e-Map is not available on your current plan.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex h-[calc(100vh-72px)] flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: "#263238" }}>
            Live Site Map
          </h1>
          <p className="text-sm text-muted-foreground">
            {entities.length} site{entities.length === 1 ? "" : "s"} ·{" "}
            <span className="text-emerald-600">{counts.green} green</span> /{" "}
            <span className="text-amber-600">{counts.amber} amber</span> /{" "}
            <span className="text-red-600">{counts.red} red</span>
            {data && data.missing_coordinates > 0 && (
              <> · {data.missing_coordinates} site(s) missing GPS (set lat/lng to plot)</>
            )}
          </p>
        </div>
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        label="the site map"
        onRetry={() => void refetch()}
      >
      <Card className="flex flex-1 flex-col overflow-hidden">
        <CardContent className="flex-1 p-0">
          <MapContainer
            center={[20.5937, 78.9629]}
            zoom={5}
            scrollWheelZoom
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds entities={entities} />
            {entities.map((e) => (
              <Marker
                key={e.id}
                position={[e.lat, e.lng]}
                icon={healthIcon(e.health_status, e.active_alarm_count > 0)}
              >
                <Popup>
                  <div className="space-y-1 text-xs">
                    <p className="text-sm font-semibold">{e.name}</p>
                    <p>
                      <strong>Health:</strong>{" "}
                      <span style={{ color: HEALTH_COLOR[e.health_status] }}>
                        {e.health_status}
                      </span>
                    </p>
                    <p>
                      <strong>Cameras:</strong> {e.camera_count} · offline{" "}
                      {e.cameras_offline}
                    </p>
                    <p>
                      <strong>Active alarms:</strong>{" "}
                      <span className={e.active_alarm_count > 0 ? "text-red-600 font-semibold" : ""}>
                        {e.active_alarm_count}
                      </span>
                    </p>
                    <button
                      className="mt-1 rounded px-2 py-1 text-white"
                      style={{ backgroundColor: "#ff6d00" }}
                      onClick={() => setCamListSite(e)}
                    >
                      View cameras →
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </CardContent>
      </Card>
      </QueryState>

      {camListSite && (
        <SiteCamerasDialog
          site={camListSite}
          onClose={() => setCamListSite(null)}
          onOpenLive={(name, hlsUrl) => setLiveCam({ name, hlsUrl })}
        />
      )}

      {liveCam && (
        <Dialog open onOpenChange={(o) => !o && setLiveCam(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{liveCam.name} — live</DialogTitle>
            </DialogHeader>
            <CameraFeedPlayer
              hlsUrl={liveCam.hlsUrl}
              cameraName={liveCam.name}
              autoplay
              controls
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SiteCamerasDialog({
  site,
  onClose,
  onOpenLive,
}: {
  site: EMapEntity;
  onClose: () => void;
  onOpenLive: (name: string, hlsUrl?: string | null) => void;
}) {
  const cams = useSiteCameras(site.id);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{site.name} — cameras</DialogTitle>
        </DialogHeader>
        <div className="max-h-80 space-y-1 overflow-auto">
          {(cams.data?.items ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No cameras at this site.</p>
          ) : (
            (cams.data?.items ?? []).map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between rounded border px-2 py-1 text-sm"
              >
                <span>
                  {c.name}{" "}
                  <span
                    className={
                      c.status === "online" ? "text-emerald-600" : "text-red-600"
                    }
                  >
                    · {c.status}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onOpenLive(c.name, c.hls_url)}
                >
                  Live
                </Button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
